import React from 'react';
import { useModal } from '../../../../components/Modal/PresentedModal';
import { t } from '../../../../locales/translate';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { Stack } from '@nerdgeschoss/shimmer-component-stack';
import { FormSearch } from '../../../../components/Form/AsyncSearch/FormSearch';
import { FormSelect } from '../../../../components/Form/Select/FormSelect';
import { Text } from '../../../../components/text/text';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { FormTextArea } from '../../../../components/Form/TextArea/FormTextArea';
import { FormSwitcher } from '../../../../components/Form/form_switcher/form_switcher';
import { FormDatepicker } from '../../../../components/Form/Datepicker/FormDatepicker';
import { FormForm } from '../../../../components/Form/FormForm';
import { graphql } from '../../../../generated';
import { useQueryHandler } from '../../../../model/graphql';
import { useMutation } from '@apollo/client';
import { eventPath } from '../../../../routes';
import { history } from '../../../../model/history';
import { handleError } from '../../../../model/notification';
import { PersonalizationRequirement } from '../../../../model/event';
import { VenueLayoutSelect } from './venue_layout_select';
import { VenueLayout } from '../../../../model/venue';
import { addHoursToDate, formatDuration, getDateRangeDuration } from '../../../../util/util';

const GET_VENUE_LAYOUT = graphql(`
  query EventVenueLayoutMapSelection($id: ID!) {
    venueLayout(id: $id) {
      id
      requiresMapSelection
    }
  }
`);

const CREATE_EVENT = graphql(`
  mutation CreateEvent(
    $name: String
    $artist: String!
    $happeningFrom: ISO8601DateTime!
    $happeningUntil: ISO8601DateTime!
    $launchSaleAt: ISO8601DateTime
    $venueLayoutId: String!
    $additionalInformation: JSON
    $description: String
    $doorTime: String
    $excludeFromFeeds: Boolean
    $legacyId: String
    $localOrganizer: String
    $personalizationRequirement: PersonalizationRequirement
    $publishedFrom: ISO8601DateTime
    $stageTime: String
    $ticketHeadline: String
    $ticketSupportLine: String
    $tourOrganizer: String
    $blockAllSeats: Boolean
  ) {
    createEvent(
      input: {
        name: $name
        artist: $artist
        happeningFrom: $happeningFrom
        happeningUntil: $happeningUntil
        venueLayoutId: $venueLayoutId
        additionalInformation: $additionalInformation
        description: $description
        doorTime: $doorTime
        excludeFromFeeds: $excludeFromFeeds
        launchSaleAt: $launchSaleAt
        legacyId: $legacyId
        localOrganizer: $localOrganizer
        personalizationRequirement: $personalizationRequirement
        publishedFrom: $publishedFrom
        stageTime: $stageTime
        ticketHeadline: $ticketHeadline
        ticketSupportLine: $ticketSupportLine
        tourOrganizer: $tourOrganizer
        blockAllSeats: $blockAllSeats
      }
    ) {
      clientMutationId
      event {
        id
      }
    }
  }
`);

const personalizationOptions: Array<{
  label: string;
  value: PersonalizationRequirement;
}> = [
  {
    label: t.page.event.create.noPersonalizationRequired,
    value: 'no_personalization_required',
  },
  {
    label: t.page.event.create.groupPersonalizationRequired,
    value: 'group_personalization_required',
  },
  {
    label: t.page.event.create.ticketPersonalizationRequired,
    value: 'ticket_personalization_required',
  },
  {
    label: t.page.event.create.contactTracingRequired,
    value: 'contact_tracing_required',
  },
  {
    label: t.page.event.create.checkInRequired,
    value: 'check_in_required',
  },
  {
    label: t.model.event.personalization.bdkv,
    value: 'bdkv',
  },
];

export type CreateEventFormFields = {
  venueId?: string;
  venueLayoutId?: string;
  blockAllSeats?: boolean;
  artist?: string;
  name?: string;
  ticketSupportLine?: string;
  ticketHeadline?: string;
  additionalInformation?: string;
  tourOrganizer?: string;
  localOrganizer?: string;
  description?: string;
  excludeFromFeeds?: boolean;
  happeningFrom?: Date;
  happeningUntil?: Date;
  launchSaleAt?: Date;
  doorTime?: string;
  stageTime?: string;
  personalizationRequirement?: PersonalizationRequirement;
  legacyId?: string;
  publishedFrom?: Date;
};

export function CreateEvent({ venueLayout }: { venueLayout?: VenueLayout }): JSX.Element {
  const { open, Modal, close } = useModal();

  const [create] = useMutation(CREATE_EVENT);
  const { fields, model, onSubmit, dirty, valid, submissionStatus } = useForm<CreateEventFormFields>({
    model: {
      venueId: venueLayout?.venue?.id,
      venueLayoutId: venueLayout?.id,
      artist: undefined,
      happeningFrom: undefined,
    },
    validations: {
      venueId: 'required',
      venueLayoutId: 'required',
      artist: 'required',
      happeningFrom: 'required',
      happeningUntil: 'required',
    },
    onSubmit: async ({ model }) => {
      if (!model.artist || !model.venueId || !model.venueLayoutId || !model.happeningFrom) {
        return;
      }

      const resp = await create({
        variables: {
          name: model.name,
          artist: model.artist,
          happeningFrom: model.happeningFrom,
          happeningUntil: model.happeningUntil,
          launchSaleAt: model.launchSaleAt,
          venueLayoutId: model.venueLayoutId,
          description: model.description,
          doorTime: model.doorTime,
          excludeFromFeeds: !!model.excludeFromFeeds,
          legacyId: model.legacyId,
          localOrganizer: model.localOrganizer,
          personalizationRequirement: model.personalizationRequirement,
          publishedFrom: model.publishedFrom,
          stageTime: model.stageTime,
          ticketHeadline: model.ticketHeadline,
          ticketSupportLine: model.ticketSupportLine,
          tourOrganizer: model.tourOrganizer,
          blockAllSeats: !!model.blockAllSeats,
          additionalInformation: model.additionalInformation
            ? {
                title: model.additionalInformation,
              }
            : undefined,
        },
      });
      const id = resp.data?.createEvent?.event.id;

      close();

      if (id) {
        history.push(eventPath(id));
      }
    },
    onSubmitError: handleError,
  });
  const { data } = useQueryHandler(GET_VENUE_LAYOUT, {
    variables: {
      id: model.venueLayoutId,
    },
    skip: !model.venueLayoutId,
  });

  return (
    <Modal open={open} title={t.page.event.create.addEvent}>
      <FormForm
        onSubmit={onSubmit}
        onClose={close}
        canSubmit={dirty && valid}
        loading={submissionStatus === 'submitting'}
      >
        <Stack gap={24}>
          <Text type="card-label-semibold">{t.page.event.create.venue}</Text>
          <FormSearch
            {...fields.venueId}
            label={t.page.event.create.venues}
            searchIn={['venue']}
            disabled={venueLayout?.id !== undefined}
          />

          {fields.venueId.value && (
            <VenueLayoutSelect
              {...fields.venueLayoutId}
              label={t.page.event.create.venueLayout}
              allowEmpty
              venueId={fields.venueId.value}
              disabled={venueLayout?.id !== undefined}
            />
          )}

          {data?.venueLayout.requiresMapSelection && (
            <FormSwitcher
              {...fields.blockAllSeats}
              label={t.page.event.create.blockAllSeats}
              labels={[t.page.event.create.yes, t.page.event.create.no]}
            />
          )}

          <Text type="card-label-semibold">{t.page.event.create.event}</Text>
          <FormTextField {...fields.artist} label={t.page.event.create.ticketline1} />
          <FormTextField {...fields.name} label={t.page.event.create.ticketline2} />
          <FormTextField {...fields.ticketSupportLine} label={t.page.event.create.ticketline3} />
          <FormTextField {...fields.ticketHeadline} label={t.page.event.create.ticketlineHeadline} />
          <FormTextField {...fields.additionalInformation} label={t.page.event.create.ticketlineFooter} />
          <FormTextField {...fields.tourOrganizer} label={t.page.event.create.tourOperator} />
          <FormTextField {...fields.localOrganizer} label={t.page.event.create.localOperator} />
          <FormTextArea {...fields.description} label={t.page.event.create.description} />
          <FormSwitcher
            {...fields.excludeFromFeeds}
            id="excludeFromFeeds"
            label={t.page.event.create.excludeFromFeeds}
            labels={[t.page.event.create.yes, t.page.event.create.no]}
          />

          <Text type="card-label-semibold">{t.page.event.create.datesAndTimes}</Text>
          <FormDatepicker
            {...fields.happeningFrom}
            label={t.page.event.create.startDate}
            disabledDays={[{ before: new Date() }]}
            hourpicker
            onChange={(date) => {
              fields.happeningFrom.onChange(date);
              if (date) {
                if (!model.happeningUntil || date > model.happeningUntil) {
                  fields.happeningUntil.onChange(addHoursToDate(date, 1));
                }
              } else {
                fields.happeningUntil.onChange(undefined);
              }
            }}
          />
          <FormDatepicker
            {...fields.happeningUntil}
            label={t.page.event.create.endDate}
            disabledDays={[
              {
                before: model.happeningFrom || new Date(),
              },
            ]}
            hourpicker
          />
          {model.happeningFrom && model.happeningUntil && (
            <p>
              {t.model.datetime.duration}:{' '}
              {formatDuration(getDateRangeDuration(model.happeningFrom, model.happeningUntil), 'minutes')}
            </p>
          )}
          <FormDatepicker
            {...fields.launchSaleAt}
            label={t.page.event.create.activeFrom}
            disabledDays={[{ before: new Date() }]}
            hourpicker
          />
          <FormTextField
            {...fields.doorTime}
            label={t.page.event.create.doorTime}
            placeholder="HH:mm"
            description={t.page.event.create.formatHHMM}
            type="custom-time"
          />
          <FormTextField
            {...fields.stageTime}
            label={t.page.event.create.stageTime}
            placeholder="HH:mm"
            description={t.page.event.create.formatHHMM}
            type="custom-time"
          />
          <FormSelect
            {...fields.personalizationRequirement}
            label={t.page.event.create.personalization}
            allowEmpty
            options={personalizationOptions}
          />
          <FormTextField {...fields.legacyId} label={t.page.event.create.legacyId} />

          <Text type="card-label-semibold">{t.page.event.create.shopSettings}</Text>
          <FormDatepicker
            {...fields.publishedFrom}
            label={t.page.event.create.publishDate}
            disabledDays={[{ after: model.happeningFrom, before: new Date() }]}
            hourpicker={true}
          />
        </Stack>
      </FormForm>
    </Modal>
  );
}
